import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  RouterProvider,
  createRouter,
  createRootRoute,
  createRoute,
  Outlet,
} from '@tanstack/react-router';
import { ThemeProvider } from 'next-themes';

import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import DownloadPage from './pages/DownloadPage';
import ConnectPage from './pages/ConnectPage';
import JoinTeamPage from './pages/JoinTeamPage';
import PrototypesPage from './pages/PrototypesPage';
import ELibraryPage from './pages/ELibraryPage';

import Layout from './components/Layout';
import EmptyLayout from './components/EmptyLayout';

import DashBoard from './desktop/Dashboard/DashBoard';
import AuthDialog from './desktop/Dashboard/auth_screen';

import { SpinnerProvider } from './Widgets/Spinner';
import { ToastProvider } from './Widgets/Toast';
import { Toaster } from '@/components/ui/sonner';
import TurnstileLoader from './components/TurnstileLoader';
import LegalLayout from './components/LegalLayout';
import PrivacyPage from './policy/PrivacyPage';
// Lazy-loaded — country-state-city (imported inside AlumniSurvery.tsx)
// bundles a ~7.7MB city.json world-cities dataset. Eagerly importing this
// meant every visitor to every page paid that parse/memory cost, not just
// the rare visitor to /alumni/update. Only fetch/evaluate it when this
// route is actually matched.
const AlumniUpdatePage = lazy(() => import('./desktop/Dashboard/Alumni/AlumniSurvery'));
import TutorialsLandingPage from './pages/TutorialsLandingPage';
import TutorialViewerPage from './pages/TutorialViewerPage';
/* ───────────────── ROOT ───────────────── */

const rootRoute = createRootRoute({
  component: Outlet,
});

/* ──────────────── LAYOUTS ──────────────── */

const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  component: Layout,
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  component: EmptyLayout,
});

/* ───────────── PUBLIC PAGES ───────────── */

const indexRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/',
  component: HomePage,
});

const aboutRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/about',
  component: AboutPage,
});

const servicesRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/services',
  component: ServicesPage,
});

const downloadRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/download',
  component: DownloadPage,
});

const connectRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/connect',
  component: ConnectPage,
});

const joinRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/join',
  component: JoinTeamPage,
});

const prototypesRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/prototypes',
  component: PrototypesPage,
});

// Placeholder destination for the mobile app's E-Library entry point until the
// real product ships. Deliberately not in the header nav — it's a deep-link target.
const elibraryRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/elibrary',
  component: ELibraryPage,
});

/* ───────────── AUTH / DASHBOARD ───────────── */

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login',
  component: AuthDialog,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/dashboard',
  component: DashBoard,
});

const alumniUpdateRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/alumni/update',
  component: () => (
    <Suspense fallback={<div className="min-h-screen bg-[#FFDE59]" />}>
      <AlumniUpdatePage />
    </Suspense>
  ),
});

/* ───────────── ROUTE TREE ───────────── */




const legalLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'legal',
  component: LegalLayout,
});


const privacyRoute = createRoute({
  getParentRoute: () => legalLayoutRoute,
  path: '/privacy',
  component: PrivacyPage,
});

/* ───────────── TUTORIALS ───────────── */

const tutorialsLandingRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/tutorials',
  component: TutorialsLandingPage,
});

const tutorialViewerRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/tutorials/$productId/$categoryId/$articleId',
  component: TutorialViewerPage,
});

const routeTree = rootRoute.addChildren([
  publicLayoutRoute.addChildren([
    indexRoute,
    aboutRoute,
    servicesRoute,
    downloadRoute,
    connectRoute,
    joinRoute,
    prototypesRoute,
    elibraryRoute,
    tutorialsLandingRoute,
    tutorialViewerRoute,
  ]),
  authLayoutRoute.addChildren([
    loginRoute,
    dashboardRoute,
    alumniUpdateRoute
  ]),

  legalLayoutRoute.addChildren([
    privacyRoute,
  ]),
]);

const router = createRouter({ routeTree });
/* ───────────── APP ───────────── */

const queryClient = new QueryClient();

export default function App() {
  return (
    <SpinnerProvider>
      <ToastProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
            <TurnstileLoader />
            <RouterProvider router={router} />
            <Toaster />
          </ThemeProvider>
        </QueryClientProvider>
      </ToastProvider>
    </SpinnerProvider>
  );
}
